/**
 * On-device prediction models: the AI part of ASSIST's decision support.
 *
 * A content pack can ship a trained model (exported to JSON) next to its rules. The model runs
 * offline on the tablet, at a declared stage of rule evaluation, and writes its probability and
 * risk band into the rule context, so rules can act on it. Design rules:
 *
 *  - The model PROMPTS, it never replaces: its output is advice with its reasons, and the rules
 *    (the guideline) keep working whether or not a model is present.
 *  - A deterministic safety layer may RAISE the band (red flags measured now), never lower it.
 *  - No score is better than a wrong score: a model whose feature contract does not match its
 *    inputs is refused at build time, and a prediction is WITHHELD when too few of its core inputs
 *    were measured.
 *  - Every prediction says how many inputs were measured (the rest use the model's defaults),
 *    which inputs pushed the risk up, and which model version produced it.
 *  - Models have the same draft/approved gating as rules.
 *
 * Runtimes: gradient-boosted trees for binary outcomes (exported as JSON trees) and
 * logistic regression. Both are plain arithmetic: no external library, nothing leaves the device.
 */
import { evaluate, parse, test } from './expr.js';
/** The functional part of an exported model: everything else in the file is dropped. */
export function functionalArtifact(a) {
    const thresholds = a.thresholds
        ? { thresholds: { amber: a.thresholds.amber, red: a.thresholds.red } }
        : {};
    if (a.kind === 'gbdt-binary')
        return {
            kind: a.kind,
            features: a.features,
            defaults: a.defaults,
            base: a.base,
            learning_rate: a.learning_rate,
            trees: a.trees,
            ...thresholds,
        };
    return {
        kind: a.kind,
        features: a.features,
        defaults: a.defaults,
        intercept: a.intercept,
        coefficients: a.coefficients,
        ...thresholds,
    };
}
/** Problems with an artifact (empty = usable). Checked at pack build and again on load. */
export function checkArtifact(a) {
    const p = [];
    const fin = (x) => typeof x === 'number' && Number.isFinite(x);
    if (!Array.isArray(a.features) || !a.features.length)
        return ['artifact has no features'];
    if (new Set(a.features).size !== a.features.length)
        p.push('artifact features are not unique');
    for (const f of a.features)
        if (!fin(a.defaults?.[f]))
            p.push(`no numeric default for feature ${f}`);
    if (a.thresholds &&
        !(0 < a.thresholds.amber && a.thresholds.amber < a.thresholds.red && a.thresholds.red < 1))
        p.push('thresholds must satisfy 0 < amber < red < 1');
    if (a.kind === 'gbdt-binary') {
        if (!fin(a.base) || !fin(a.learning_rate))
            p.push('base and learning_rate must be numbers');
        if (!Array.isArray(a.trees) || !a.trees.length)
            p.push('no trees');
        const walk = (n, depth, where) => {
            if (depth > 16) {
                p.push(`${where}: deeper than 16`);
                return;
            }
            if ('v' in n) {
                if (!fin(n.v))
                    p.push(`${where}: leaf value not a number`);
                return;
            }
            if (!Number.isInteger(n.f) || n.f < 0 || n.f >= a.features.length)
                p.push(`${where}: feature index ${n.f} out of range`);
            if (!fin(n.t))
                p.push(`${where}: threshold not a number`);
            if (!n.l || !n.r) {
                p.push(`${where}: missing branch`);
                return;
            }
            walk(n.l, depth + 1, where);
            walk(n.r, depth + 1, where);
        };
        (a.trees ?? []).forEach((t, i) => walk(t, 0, `tree ${i}`));
    }
    else if (a.kind === 'logistic') {
        if (!fin(a.intercept))
            p.push('intercept must be a number');
        for (const f of a.features)
            if (!fin(a.coefficients?.[f]))
                p.push(`no coefficient for ${f}`);
    }
    else
        p.push(`unknown model kind ${a.kind}`);
    return p.slice(0, 20);
}
const sigmoid = (z) => 1 / (1 + Math.exp(-z));
function evalTree(n, x) {
    let node = n;
    while (!('v' in node))
        node = x[node.f] <= node.t ? node.l : node.r;
    return node.v;
}
/** Probability for a full feature vector (missing values already replaced by defaults). */
export function rawPredict(a, x) {
    if (a.kind === 'gbdt-binary') {
        let z = a.base;
        for (const t of a.trees)
            z += a.learning_rate * evalTree(t, x);
        return sigmoid(z);
    }
    let z = a.intercept;
    a.features.forEach((f, i) => {
        z += a.coefficients[f] * x[i];
    });
    return sigmoid(z);
}
const RANK = { green: 0, amber: 1, red: 2 };
const astCache = new Map();
const ast = (src) => {
    let a = astCache.get(src);
    if (!a) {
        a = parse(src);
        astCache.set(src, a);
    }
    return a;
};
const toNumber = (v) => typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'boolean' ? (v ? 1 : 0) : null;
export function bandOf(p, cut) {
    return p >= cut.red ? 'red' : p >= cut.amber ? 'amber' : 'green';
}
/** Run one model against the rule context. Null when `when` does not hold. */
export function predict(m, ctx) {
    if (m.when && !test(ast(m.when), ctx))
        return null;
    const a = m.artifact;
    const values = {};
    for (const f of a.features) {
        const src = m.inputs[f];
        values[f] = src ? toNumber(evaluate(ast(src), ctx)) : null;
    }
    const measured = a.features.filter((f) => values[f] !== null);
    const missing = a.features.filter((f) => values[f] === null);
    const base = {
        modelId: m.id,
        version: m.version,
        title: m.title,
        measured,
        missing,
        draft: m.status === 'draft',
    };
    const haveCore = m.required.filter((f) => values[f] !== null).length;
    if (haveCore < m.min_required)
        return {
            ...base,
            state: 'withheld',
            probability: null,
            band: null,
            modelBand: null,
            escalatedBy: [],
            drivers: [],
        };
    const x = a.features.map((f) => values[f] ?? a.defaults[f]);
    const p = rawPredict(a, x);
    const cut = m.bands ?? a.thresholds ?? { amber: 0.33, red: 0.6 };
    const modelBand = bandOf(p, cut);
    let band = modelBand;
    const escalatedBy = [];
    for (const e of m.escalate ?? []) {
        if (!test(ast(e.when), ctx))
            continue;
        escalatedBy.push(e.reason);
        if (RANK[e.to] > RANK[band])
            band = e.to;
    }
    // Why: how much each measured input moves the probability compared with the model's default.
    const drivers = measured
        .map((f) => {
        const i = a.features.indexOf(f);
        const alt = [...x];
        alt[i] = a.defaults[f];
        return {
            feature: f,
            label: m.labels?.[f] ?? f,
            value: values[f],
            effect: p - rawPredict(a, alt),
        };
    })
        .filter((d) => d.effect > 0.005)
        .sort((d1, d2) => d2.effect - d1.effect)
        .slice(0, 4);
    return { ...base, state: 'scored', probability: p, band, modelBand, escalatedBy, drivers };
}
//# sourceMappingURL=ai.js.map